// The stop-an-epic write flow (#1906) — the state half of <StopPlanModal/>.
// Sibling of `taskStop.ts` rather than a mode of it: the answers differ, and
// that difference is the whole point of the surface.
//
// Stopping a TASK is one verdict. Stopping an EPIC is three lists — what stopped,
// what had already finished, and what KEPT RUNNING because a review in flight
// cannot be interrupted. Folding those into "stopped ✓" would be the quieter
// success the route deliberately refuses to give.

import {
  isPlanFilesGroupId,
  type PlanProjection,
  type PlanProjectionGroup,
} from '@developerz/domain';
import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from './api';
import { canStopTask } from './fleet';
import { cancelPlan, type PlanCancelResult } from './plan';

/**
 * Is there anything on this epic a stop would actually reach?
 *
 * The same absence rule the queue rows follow, folded over the slices: a control
 * offered on an epic whose every slice has finished can only ever answer "nothing
 * to stop", which teaches the reader the button is broken. A slice with no task
 * yet counts as nothing to stop — the route cancels EXISTING children, and an
 * undispatched group has none.
 *
 * Reads `group.task.status` through `canStopTask`, so this view and the queue
 * board cannot disagree about what terminal means.
 */
export function planHasStoppableWork(projection: PlanProjection): boolean {
  return stoppableSlices(projection).length > 0;
}

/** The slices a stop would actually reach — the population every count below is over. */
function stoppableSlices(projection: PlanProjection): readonly PlanProjectionGroup[] {
  return projection.groups.filter((g) => g.task !== null && canStopTask(g.task.status));
}

/**
 * What the epic confirm NAMES. Same rule as the task confirm: an id is not enough,
 * so this carries the count it would stop and a human headline for the epic.
 *
 * The headline is the LOWEST-POSITION slice's title AMONG THE GROUPS THAT ARE
 * NOT #5172's `plan-files` bookkeeping group (#5189) — because a plan has no
 * title column of its own — the id is caller-minted — and that is exactly the
 * `leadTitle` convention `selectPlanHeadlines` (`@developerz/db`) and the
 * epics index already use to make a plan scannable. A plan whose ONLY group
 * IS the plan-files one (nothing has been approved past it yet) falls back
 * to ITS title — the same one-group exception the server applies.
 */
export type PlanStopTarget = {
  readonly planId: string;
  readonly lead: string;
  readonly count: number;
};

export function planStopTarget(projection: PlanProjection): PlanStopTarget | null {
  const stoppable = stoppableSlices(projection);
  if (stoppable.length === 0) return null;
  const ordered = [...projection.groups].sort((a, b) => a.position - b.position);
  const nonPlanFiles = ordered.filter((g) => !isPlanFilesGroupId(g.groupId));
  const lead = (nonPlanFiles[0] ?? ordered[0])?.title ?? '';
  return { planId: projection.planId, lead, count: stoppable.length };
}

export type PlanStopController = {
  /** The epic awaiting confirmation, or null. Feed to <StopPlanModal plan=…/>. */
  readonly target: () => PlanStopTarget | null;
  /** Failure copy for the surface's <Toast/>, or null. */
  readonly error: () => string | null;
  readonly ask: (target: PlanStopTarget) => void;
  readonly close: () => void;
  readonly clearError: () => void;
  /** Stop the epic. A second call while one is in flight is dropped. */
  readonly confirm: () => void;
};

/**
 * Stop state for one epic view. `onStopped` receives the server's THREE lists
 * intact — the caller decides what to say, because "stopped 4 slices, 2 reviews
 * kept running" and "nothing left to stop" are different sentences and only the
 * surface knows which line it has room for.
 *
 * A failure leaves the dialog OPEN with `error()` set, same rule as `taskStop`.
 */
export function createPlanStop(input: {
  readonly planId: () => string | null;
  readonly token: string | null;
  readonly onStopped: (result: PlanCancelResult) => void;
}): PlanStopController {
  const [target, setTarget] = createSignal<PlanStopTarget | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  let busy = false;

  const confirm = (): void => {
    const id = input.planId();
    if (id === null || busy) return;
    busy = true;
    setError(null);
    void cancelPlan(id, input.token)
      .then((result) => {
        setTarget(null);
        input.onStopped(result);
      })
      .catch((err: unknown) => setError(errorReason(err, t('fleet.stop.plan_error'))))
      .finally(() => {
        busy = false;
      });
  };

  return {
    target,
    error,
    ask: (next) => {
      setError(null);
      setTarget(next);
    },
    close: () => setTarget(null),
    clearError: () => setError(null),
    confirm,
  };
}
